import { useEffect, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';

// Hooks
import { useGooglePlacesApi } from 'hooks/use-google-places-api';
import { useStores } from 'hooks/use-stores';

// Constants
import { TOPLoggedError } from 'constants/Errors';

import Customer from 'models/Customer';
import { TODO } from 'utils/Types';
import { MapDiv } from '../styles';
import InfoWindowContent from './InfoWindowContent';

const mapMarkers = [];
const infoWindows = [];

const GoogleMap = ({
  customersById,
  currentLocation,
}: {
  currentLocation: TODO;
  customersById: Record<string, Customer>;
}) => {
  const [map, setMap] = useState(null);
  const [customersCoordsToMap, setCustomersCoordsToMap] = useState([]);
  const mapRef = useRef(null);
  const { hostStore } = useStores();
  const { assets } = hostStore;
  const { favicon } = assets;

  const GooglePlacesAPI = useGooglePlacesApi();

  /**
   * Filter out all customers that do not have physical locations configured. You cannot draw a marker on a map without
   * a Google Places location which provides the latitude and longitude.
   */
  useEffect(() => {
    const customersCoordsToMapCalc = Object.values(customersById)
      .map((customer) => {
        try {
          if (!customer.physical_address?.latitude || !customer.physical_address?.longitude) {
            throw new TOPLoggedError(
              `Customer ${customer?.customer_name} does not have a physical address configured and therefore you cannot place a marker on the map.`,
              {
                endpoint: 'customersCoordsToMapCalc',
                customer_id: customer?.customer_id,
              }
            );
          } else {
            return {
              customer_name: customer.customer_name,
              customer_id: customer.customer_id,
              max_delivery_miles: customer.max_delivery_miles,
              physical_address: customer.physical_address,
              lat: customer.physical_address?.latitude,
              lng: customer.physical_address?.longitude,
              show: true,
              vendees: customer.vendees,
              store_hours: customer.store_hours,
            };
          }
        } catch (error) {
          return null;
        }
      })
      .filter((customer) => customer);

    setCustomersCoordsToMap(customersCoordsToMapCalc);
  }, [customersById]);

  // Load Map
  useEffect(() => {
    if (!map && mapRef && customersCoordsToMap?.length > 0 && GooglePlacesAPI.maps) {
      const newMap = GooglePlacesAPI.initInteractiveMap(
        mapRef?.current,
        customersCoordsToMap[Math.floor(Math.random() * customersCoordsToMap.length)],
        10
      );
      setMap(newMap);
      customersCoordsToMap.forEach((customer) => {
        // Place Marker
        const marker = GooglePlacesAPI.drawMarkerOnMap(customer, newMap, favicon);
        mapMarkers.push(marker);

        // Place Info Window on Map
        // const locationInfo = locations.filter((loc) => loc.customer === customerCoords.customer_id)
        const markerInfoWindow = GooglePlacesAPI.drawInfoWindow();
        infoWindows.push(markerInfoWindow);

        // Remove highlighted class on the location card if the user dismisses the infoWindow
        markerInfoWindow.addListener('closeclick', () => {
          // const customerIdToScrollTo = ''
        });

        marker.addListener('click', () => {
          // Add an evalAsync because this event listener runs outside of angularJS's event loop.
          (() => {
            newMap.setZoom(13);
            newMap.panTo(marker.getPosition());
            // const clickedCoords = { latitude: marker.getPosition().lat(), longitude: marker.getPosition().lng() }

            // Open Info Window and close all other windows
            infoWindows.forEach((infoWindow) => infoWindow.close());
            const { customer_name, phone_for_patrons, physical_address, store_hours } = customer;

            const content = renderToString(
              <InfoWindowContent
                customer_name={customer_name}
                phone_for_patrons={phone_for_patrons}
                physical_address={physical_address}
                store_hours={store_hours}
              />
            );
            markerInfoWindow.setContent(content);

            markerInfoWindow.open(newMap, marker);
          })();
        });
      });
    }
  }, [GooglePlacesAPI, customersCoordsToMap, map, favicon, mapRef]);

  useEffect(() => {
    if (currentLocation && map) {
      GooglePlacesAPI.setSearchLocation(currentLocation);
      const coords = { lat: currentLocation.geometry.location.lat(), lng: currentLocation.geometry.location.lng() };

      const newCurrentLocationMarker = GooglePlacesAPI.placeMarkerAndPanTo(coords, map);
      GooglePlacesAPI.resetSearchLocationMarker(newCurrentLocationMarker, map);
    }
  }, [GooglePlacesAPI, map, currentLocation]);

  return <MapDiv ref={mapRef} id="map" />;
};

export default GoogleMap;
